import { state } from './state.js';
import * as API from './api.js';
import { handleLibSelect } from './library.js';
import { selectSourcePhotoById } from './source_photos.js';
import { refreshSettingsVisibility } from './workflow_selection.js';

const CREATE_DRAFT_KEY_PREFIX = 'mosaic_create_draft_v1';
const CREATE_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const CREATE_DRAFT_SAVE_DEBOUNCE_MS = 220;
const SETTINGS_MODE_SELECTOR = '#settingsMode .seg-btn[data-mode]';
const SETTINGS_IDS = [
    'presetSelect',
    'simpleDetail',
    'simpleColor',
    'simpleVariety',
    'modeSelect',
    'adaptiveSquareMinSizeInput',
    'qualityToggle',
    'matcherBackendSelect',
    'allowFlips',
    'colorMode',
    'legacyHighresDraftToggle',
    'scaleInput',
    'thInput',
    'colorShare',
    'noiseFactor',
    'rpInput',
];

let _draftListenersBound = false;
let _saveTimer = 0;

function _currentSessionId() {
    let anonId = '';
    try {
        anonId = String(localStorage.getItem('mosaic_user_id') || '').trim();
    } catch (_e) {}
    const userId = String(state.userId || '').trim();
    return userId || anonId || 'session';
}

function _storageKey() {
    return `${CREATE_DRAFT_KEY_PREFIX}:${_currentSessionId()}`;
}

function _readDraftPayload() {
    try {
        const raw = sessionStorage.getItem(_storageKey());
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
    } catch (_e) {
        return null;
    }
}

function _writeDraftPayload(payload) {
    try {
        sessionStorage.setItem(_storageKey(), JSON.stringify(payload));
        return true;
    } catch (_e) {
        return false;
    }
}

function _collectSettingsSnapshot() {
    const snapshot = {};
    const modeBtn = document.querySelector(`${SETTINGS_MODE_SELECTOR}.active`);
    snapshot.settings_mode = String((modeBtn && modeBtn.dataset && modeBtn.dataset.mode) || '').trim();

    SETTINGS_IDS.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        if (el.type === 'checkbox') {
            snapshot[id] = Boolean(el.checked);
            return;
        }
        snapshot[id] = String(el.value || '');
    });

    return snapshot;
}

function _applySettingsSnapshot(settings) {
    if (!settings || typeof settings !== 'object') return false;
    let applied = false;

    const mode = String(settings.settings_mode || '').trim().toLowerCase();
    if (mode) {
        const modeBtn = document.querySelector(`${SETTINGS_MODE_SELECTOR}[data-mode="${mode}"]`);
        if (modeBtn && !modeBtn.classList.contains('active')) {
            modeBtn.click();
            applied = true;
        }
    }

    SETTINGS_IDS.forEach((id) => {
        if (!(id in settings)) return;
        const el = document.getElementById(id);
        if (!el) return;
        if (el.type === 'checkbox') {
            const next = Boolean(settings[id]);
            if (Boolean(el.checked) === next) return;
            el.checked = next;
            el.dispatchEvent(new Event('change', { bubbles: true }));
            applied = true;
            return;
        }
        const nextValue = String(settings[id] || '');
        if (String(el.value || '') === nextValue) return;
        el.value = nextValue;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        applied = true;
    });

    return applied;
}

function _clearSaveTimer() {
    if (_saveTimer) {
        window.clearTimeout(_saveTimer);
        _saveTimer = 0;
    }
}

export function persistCreateDraft(reason = 'manual') {
    const payload = {
        version: 1,
        saved_at: Date.now(),
        reason: String(reason || ''),
        source_photo_id: String(state.selectedSourcePhotoId || '').trim(),
        library_id: String(state.selectedLibraryId || '').trim(),
        settings: _collectSettingsSnapshot(),
    };
    _writeDraftPayload(payload);
}

function _scheduleDraftSave(reason = 'update') {
    _clearSaveTimer();
    _saveTimer = window.setTimeout(() => {
        _saveTimer = 0;
        persistCreateDraft(reason);
    }, CREATE_DRAFT_SAVE_DEBOUNCE_MS);
}

export async function restoreCreateDraftState() {
    const draft = _readDraftPayload();
    if (!draft) return false;

    const savedAt = Number(draft.saved_at || 0);
    if (!Number.isFinite(savedAt) || savedAt <= 0 || (Date.now() - savedAt) > CREATE_DRAFT_MAX_AGE_MS) {
        return false;
    }

    let sourceApplied = false;
    let libraryApplied = false;
    let settingsApplied = false;

    const sourcePhotoId = String(draft.source_photo_id || '').trim();
    if (sourcePhotoId) {
        sourceApplied = selectSourcePhotoById(sourcePhotoId);
    }

    const libraryId = String(draft.library_id || '').trim();
    if (libraryId) {
        const libraryExists = (state.libraries || []).some((lib) => String((lib && lib.id) || '') === libraryId);
        if (libraryExists) {
            try {
                await handleLibSelect(libraryId);
                libraryApplied = true;
            } catch (_e) {
                libraryApplied = false;
            }
        }
    }

    settingsApplied = _applySettingsSnapshot(draft.settings || {});
    refreshSettingsVisibility();

    const restored = Boolean(sourceApplied || libraryApplied || settingsApplied);
    if (restored) {
        API.logUiFlow('create_draft', 'restore', 'ok', {
            source_applied: sourceApplied,
            library_applied: libraryApplied,
            settings_applied: settingsApplied,
        }).catch(() => {});
    }
    return restored;
}

export function initCreateDraftPersistence() {
    if (_draftListenersBound) return;
    _draftListenersBound = true;

    const configCard = document.getElementById('configCard');
    if (configCard) {
        configCard.addEventListener('input', () => _scheduleDraftSave('settings_input'));
        configCard.addEventListener('change', () => _scheduleDraftSave('settings_change'));
    }

    document.addEventListener('source:uploaded', () => _scheduleDraftSave('source_uploaded'));
    document.addEventListener('library:selected-updated', () => _scheduleDraftSave('library_selected'));
    document.addEventListener('workflow:draft_dirty', () => _scheduleDraftSave('workflow_dirty'));
    window.addEventListener('beforeunload', () => {
        persistCreateDraft('beforeunload');
    });
}
